import { Button } from "@/components/ui/button";

// v4 registry no longer exports ButtonProps — derive from the component.
type ButtonProps = React.ComponentProps<typeof Button>;
import { format } from "date-fns";
import { VersionTimelineSheet } from "./column-publish-log";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { optimisticList, optimisticAtom, putFetcher } from "@/lib/swr";
import React, { useMemo } from "react";
import useSWRMutation from "swr/mutation";
import { nextsFor, stateInfo } from "@/constants/version-state";
import { useApp } from "@/hooks/apps";
import { RefreshCwIcon } from "lucide-react";
import type { Version } from "@/types/domain";
import { VERSION_MILESTONES } from "@/constants/version-milestones";
import { DATE_SHORT } from "@/lib/date";

export const StateWithAction = ({
  version,
  variant,
  className,
}: {
  version: Version;
  variant?: ButtonProps["variant"];
  className?: string;
}) => {
  const { trigger, isMutating } = useSWRMutation(`/versions/${version.id}`, putFetcher<Record<string, unknown>>, {
    onSuccess: (data) => {
      optimisticList(`/versions`, version.id, data);
      optimisticAtom(`/versions/${version.id}`, data);
    }
  });

  // Transitions follow the app's configured flow when present (App Settings
  // → Version flow); apps without one keep the built-in graph.
  const app = useApp(version.pid);
  const nexts = nextsFor(app?.versionStateFlow, version.state);

  const handleChange = (state: number) => {
    if (state === version.state) return;
    if (isMutating) return;
    // Optimistic update first
    optimisticList(`/versions`, version.id, { state });
    optimisticAtom(`/versions/${version.id}`, { state });
    trigger({ state });
  };

  if (isMutating) {
    return (
      <Button
        variant={variant ?? "ghost"}
        size="sm"
        className={cn("flex px-2 py-0 h-6", className)}
        disabled
      >
        <RefreshCwIcon className="mr-2 h-4 w-4 animate-spin" />
        <span>Loading…</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <StateButton
          stateCode={version.state}
          {...(variant ? { variant } : {})}
          className={className}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[160px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {nexts.map((code) => {
          const next = stateInfo(code);
          if (!next) return null;
          return (
            <DropdownMenuItem
              key={code}
              onClick={() => handleChange(next.code)}
              className="cursor-pointer"
            >
              {next.icon && <next.icon className="mr-2 h-4 w-4" />}
              {next.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};


export const VersionProgress = ({
  version,
  readOnly,
}: {
  version: Version;
  readOnly?: boolean;
}) => {
  const inner = VERSION_MILESTONES.map((step, i) => {
    const v = version[step.field];
    return (
      <React.Fragment key={step.field}>
        {i > 0 && <span className="text-muted-foreground/40">›</span>}
        <span
          className={cn(
            "inline-block w-12 text-center tabular-nums",
            !v && "text-destructive"
          )}
        >
          {v ? format(new Date(v), DATE_SHORT) : "—"}
        </span>
      </React.Fragment>
    );
  });

  // Read-only (list): pure display, does not open the editable timeline
  if (readOnly) {
    return (
      <div className="inline-flex max-w-full items-center gap-1.5 px-1 py-0.5 text-xs">
        {inner}
      </div>
    );
  }

  return (
    <VersionTimelineSheet version={version}>
      <Button
        variant="ghost"
        className="inline-flex h-auto max-w-full items-center justify-start gap-1.5 rounded px-1 py-0.5 text-xs font-normal"
      >
        {inner}
      </Button>
    </VersionTimelineSheet>
  );
};

export const StateButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { stateCode?: number }
>(({ stateCode, className, ...props }, ref) => {
  const state = useMemo(() => stateInfo(stateCode), [stateCode]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      className={cn("flex gap-1 px-2 py-0 h-6", state?.color, className)}
      {...props}
    >
      {state?.icon && <state.icon className="h-4 w-4" />}
      <span>{state?.label}</span>
    </Button>
  );
});
StateButton.displayName = "VersionStateButton";
